import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams } from "@reach/router";
import { useSelector } from "react-redux";
import { Text } from "@ifgengineering/component-library";
import { StringParam, useQueryParam, BooleanParam } from "use-query-params";
import { navigate } from "gatsby";
import EisFundPage from "./EisFundPage";
import { useEisFundInvestments } from "./useEisFundInvestments";
import { EisFundFilterValues } from "./EisFundFilterModal";
import { ErrorContainer, ErrorBackButton } from "./styled";
import {
  commitmentsByDealSelector,
  neverLoadedCommitmentsSelector,
} from "@state/commitment/commitment.selectors";
import { fetchCommitmentsByDealId } from "@state/commitment/commitment.actions";
import { RootState } from "@state/rootReducer";
import { useAppDispatch } from "@state/store";
import { Commitment } from "@ifgengineering/client-invest-sdk";
import useDebounce from "@hooks/useDebounce";
import { EisFundStartup, InvestmentStatus, TaxBenefit } from "./types";

interface EisFundPageContainerProps {
  path?: string;
}

const SEARCH_DEBOUNCE_DELAY = 400;

const DEFAULT_FILTERS: EisFundFilterValues = {
  taxBenefit: "all",
  investmentStatuses: [],
};

const parseStatusLabelNames = (
  value: string | null | undefined
): InvestmentStatus[] => {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean) as InvestmentStatus[];
};

const parseTaxBenefit = (
  value: boolean | null | undefined
): TaxBenefit | "all" => {
  if (value === true) return "EIS";
  if (value === false) return "None";
  return "all";
};

const taxBenefitToBoolean = (
  taxBenefit: TaxBenefit | "all"
): boolean | undefined => {
  if (taxBenefit === "EIS" || taxBenefit === "SEIS") return true;
  if (taxBenefit === "None") return false;
  return undefined;
};

const EisFundPageContainer: React.FC<EisFundPageContainerProps> = () => {
  const { dealId } = useParams();
  const dispatch = useAppDispatch();

  const [commitmentIdParam, setCommitmentIdParam] = useQueryParam(
    "commitmentId",
    StringParam
  );
  const [dealNameParam] = useQueryParam("dealName", StringParam);
  const [statusLabelNamesParam] = useQueryParam(
    "statusLabelNames",
    StringParam
  );
  const [hasTaxBenefitParam] = useQueryParam("hasTaxBenefit", BooleanParam);

  const neverLoadedCommitments = useSelector(neverLoadedCommitmentsSelector);
  const allCommitments = useSelector<RootState, Commitment[]>((state) =>
    commitmentsByDealSelector(state, dealId as string)
  );

  useEffect(() => {
    if (neverLoadedCommitments && dealId) {
      dispatch(fetchCommitmentsByDealId(dealId as string));
    }
  }, [dispatch, dealId, neverLoadedCommitments]);

  const sortedCommitments = useMemo(() => {
    return [...allCommitments].sort((a, b) => {
      const getOrder = (c: Commitment) => {
        if (c.ifIsaAccountId) return 1;
        if (c.entityId) return 2;
        return 0;
      };
      return getOrder(a) - getOrder(b);
    });
  }, [allCommitments]);

  const commitmentId = commitmentIdParam || sortedCommitments[0]?.id;

  const [dealNameInput, setDealNameInput] = useState(dealNameParam || "");
  const debouncedDealName = useDebounce(dealNameInput, SEARCH_DEBOUNCE_DELAY);

  const [filters, setFilters] = useState<EisFundFilterValues>(() => ({
    taxBenefit: parseTaxBenefit(hasTaxBenefitParam),
    investmentStatuses: parseStatusLabelNames(statusLabelNamesParam),
  }));

  const {
    investments,
    isLoading,
    isLoadingMore,
    error,
    hasNextPage,
    totalCount,
    loadMore,
    refetch,
  } = useEisFundInvestments({
    dealId: dealId ?? "",
    commitmentId: commitmentId ?? "",
    dealName: debouncedDealName || undefined,
    taxBenefit: filters.taxBenefit,
    statusLabelNames:
      filters.investmentStatuses.length > 0
        ? filters.investmentStatuses
        : undefined,
    skip: !dealId || !commitmentId,
  });

  const isSearching =
    dealNameInput !== debouncedDealName ||
    (isLoading && Boolean(debouncedDealName));

  // All useCallback hooks must be called before any conditional returns
  const updateUrlParams = useCallback(
    (newDealName: string, newFilters: EisFundFilterValues) => {
      const url = new URL(window.location.href);

      if (newDealName) {
        url.searchParams.set("dealName", newDealName);
      } else {
        url.searchParams.delete("dealName");
      }

      if (newFilters.investmentStatuses.length > 0) {
        url.searchParams.set(
          "statusLabelNames",
          newFilters.investmentStatuses.join(",")
        );
      } else {
        url.searchParams.delete("statusLabelNames");
      }

      const hasTaxBenefit = taxBenefitToBoolean(newFilters.taxBenefit);
      if (hasTaxBenefit !== undefined) {
        url.searchParams.set("hasTaxBenefit", String(hasTaxBenefit));
      } else {
        url.searchParams.delete("hasTaxBenefit");
      }

      window.history.replaceState(null, "", url.toString());
    },
    []
  );

  const handleDealNameChange = useCallback(
    (value: string) => {
      setDealNameInput(value);
      updateUrlParams(value, filters);
    },
    [filters, updateUrlParams]
  );

  const handleFiltersApply = useCallback(
    (newFilters: EisFundFilterValues) => {
      setFilters(newFilters);
      updateUrlParams(dealNameInput, newFilters);
    },
    [dealNameInput, updateUrlParams]
  );

  const handleFiltersReset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    updateUrlParams(dealNameInput, DEFAULT_FILTERS);
  }, [dealNameInput, updateUrlParams]);

  const handleBack = useCallback(() => {
    navigate(`/app/deals/${dealId}?commitmentId=${commitmentId}`);
  }, [dealId, commitmentId]);

  const handleEisDocumentsClick = useCallback(() => {
    navigate(`/app/documents?dealId=${dealId}&referrer=eisFund`);
  }, [dealId, commitmentId]);

  const handleCommitmentChange = useCallback(
    (newCommitmentId: string) => {
      setCommitmentIdParam(newCommitmentId);
      setFilters(DEFAULT_FILTERS);
      setDealNameInput("");
      updateUrlParams("", DEFAULT_FILTERS);
    },
    [setCommitmentIdParam, updateUrlParams]
  );

  const handleStartupClick = useCallback(
    (startup: EisFundStartup) => {
      navigate(
        `/app/deals/${dealId}/eis-fund/startup/${startup.id}?commitmentId=${commitmentId}`
      );
    },
    [dealId, commitmentId]
  );

  // Conditional returns come AFTER all hooks
  if (!dealId || !commitmentId) {
    return (
      <ErrorContainer>
        <Text type="H24" color="SLATE800">
          Missing required parameters
        </Text>
        <Text type="P16" color="SLATE600">
          {!dealId
            ? "A deal ID is required to view EIS Fund investments."
            : "A commitment ID is required to view EIS Fund investments."}
        </Text>
        <ErrorBackButton onClick={() => navigate(-1)}>Go Back</ErrorBackButton>
      </ErrorContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Text type="H24" color="ERROR800">
          Error loading investments
        </Text>
        <Text type="P16" color="SLATE600">
          {error || "Failed to load EIS Fund investments. Please try again."}
        </Text>
        <ErrorBackButton onClick={() => refetch()}>Retry</ErrorBackButton>
      </ErrorContainer>
    );
  }

  return (
    <EisFundPage
      startups={investments}
      loading={isLoading}
      loadingMore={isLoadingMore}
      hasMore={hasNextPage}
      total={totalCount}
      onLoadMore={loadMore}
      searchQuery={dealNameInput}
      onSearchChange={handleDealNameChange}
      isSearching={isSearching}
      filters={filters}
      onFiltersApply={handleFiltersApply}
      onFiltersReset={handleFiltersReset}
      onBack={handleBack}
      onEisDocumentsClick={handleEisDocumentsClick}
      commitments={sortedCommitments}
      selectedCommitmentId={commitmentId}
      onCommitmentChange={handleCommitmentChange}
      onStartupClick={handleStartupClick}
    />
  );
};

export default EisFundPageContainer;
